import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, EyeOff, Undo2, Bell, CheckCircle, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";

interface ApprovalActivity {
  id: string;
  campaign_id: string;
  media_owner_name: string;
  status: string;
  created_at: string;
  reviewed_at: string | null;
  dismissed_at: string | null;
  campaign_name?: string;
}

const MEDIA_OWNERS = ['All', 'Alan', 'Waki', 'Attention', 'TableAds', 'Rotana Signs'];

const ActivityDismissalPanel = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activities, setActivities] = useState<ApprovalActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState("All");
  const [showDismissed, setShowDismissed] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('media_owner_campaign_approvals')
        .select('id, campaign_id, media_owner_name, status, created_at, reviewed_at, dismissed_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (selectedOwner !== 'All') {
        query = query.eq('media_owner_name', selectedOwner);
      }

      if (!showDismissed) {
        query = query.is('dismissed_at', null);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching activities:', error);
        return;
      }

      // Fetch campaign names
      const campaignIds = [...new Set((data || []).map(a => a.campaign_id))];
      const { data: campaigns } = await supabase
        .from('saved_campaigns')
        .select('id, campaign_name, brand_name')
        .in('id', campaignIds);

      const campaignMap = new Map((campaigns || []).map(c => [c.id, c.campaign_name || c.brand_name || c.id.slice(0, 8)]));

      setActivities((data || []).map(a => ({
        ...a,
        campaign_name: campaignMap.get(a.campaign_id) || a.campaign_id.slice(0, 8),
      })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [selectedOwner, showDismissed]);

  const handleDismiss = async (id: string) => {
    setActionLoading(id);
    const { error } = await supabase
      .from('media_owner_campaign_approvals')
      .update({ dismissed_at: new Date().toISOString(), dismissed_by: user?.id } as any)
      .eq('id', id);

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setActivities(prev => prev.map(a => a.id === id ? { ...a, dismissed_at: new Date().toISOString() } : a));
      if (!showDismissed) {
        setActivities(prev => prev.filter(a => a.id !== id));
      }
      toast({ title: language === 'ar' ? 'تم الإخفاء' : 'Dismissed', description: language === 'ar' ? 'تم إخفاء النشاط من سجل مالك الوسائط' : 'Activity hidden from media owner feed' });
    }
    setActionLoading(null);
  };

  const handleRestore = async (id: string) => {
    setActionLoading(id);
    const { error } = await supabase
      .from('media_owner_campaign_approvals')
      .update({ dismissed_at: null, dismissed_by: null } as any)
      .eq('id', id);

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setActivities(prev => prev.map(a => a.id === id ? { ...a, dismissed_at: null } : a));
      toast({ title: language === 'ar' ? 'تمت الاستعادة' : 'Restored', description: language === 'ar' ? 'تم استعادة النشاط' : 'Activity restored to feed' });
    }
    setActionLoading(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-destructive" />;
      default: return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {language === 'ar' ? 'إدارة نشاط مالكي الوسائط' : 'Media Owner Activity Management'}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedOwner} onValueChange={setSelectedOwner}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEDIA_OWNERS.map(mo => (
                <SelectItem key={mo} value={mo}>{mo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant={showDismissed ? "default" : "outline"}
            onClick={() => setShowDismissed(!showDismissed)}
          >
            <EyeOff className="h-4 w-4 mr-1" />
            {language === 'ar' ? 'المخفية' : 'Dismissed'}
          </Button>
          <Button size="sm" variant="outline" onClick={fetchActivities}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {language === 'ar' ? 'لا توجد أنشطة' : 'No activity items found'}
          </div>
        ) : (
          <div className="space-y-2">
            {activities.map(activity => (
              <div
                key={activity.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                  activity.dismissed_at 
                    ? 'bg-muted/30 border-border/30 opacity-60' 
                    : 'bg-card border-border/60'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getStatusIcon(activity.status)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{activity.campaign_name}</span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{activity.media_owner_name}</Badge>
                      <Badge variant="secondary" className="text-[10px] flex-shrink-0 capitalize">{activity.status}</Badge>
                      {activity.dismissed_at && (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground flex-shrink-0">
                          <EyeOff className="h-3 w-3 mr-1" />
                          {language === 'ar' ? 'مخفي' : 'Dismissed'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(activity.created_at), 'MMM d, yyyy · h:mm a')}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {activity.dismissed_at ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(activity.id)}
                      disabled={actionLoading === activity.id}
                      className="gap-1.5"
                    >
                      {actionLoading === activity.id
                        ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-current" />
                        : <Undo2 className="h-3.5 w-3.5" />
                      }
                      {language === 'ar' ? 'استعادة' : 'Restore'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDismiss(activity.id)}
                      disabled={actionLoading === activity.id}
                      className="gap-1.5 text-muted-foreground hover:text-destructive"
                    >
                      {actionLoading === activity.id
                        ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-current" />
                        : <EyeOff className="h-3.5 w-3.5" />
                      }
                      {language === 'ar' ? 'إخفاء' : 'Dismiss'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityDismissalPanel;
